import {
  CaretRightOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  PauseOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import type {
  TerminalRecordingSession,
  TerminalRecordingSessionView as TerminalRecordingView,
} from "@p0security/shared/integrations/sessions/types";
import {
  Button,
  Empty,
  Flex,
  List,
  Modal,
  Select,
  Slider,
  Tag,
  Typography,
} from "antd";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { SessionViewHeader } from "./SessionViewHeader";
import { XtermPlayer } from "./XtermPlayer";
import { inferTerminalDimensions } from "./terminalPlayback";

const PLAYBACK_SPEEDS = [1, 5, 10, 50] as const;

const PLAYBACK_SPEED_OPTIONS = PLAYBACK_SPEEDS.map((speed) => ({
  label: `${speed}x`,
  value: speed,
}));

const CONTROLS_HIDE_DELAY_MS = 750;

const SLIDER_STYLES = {
  rail: { background: "rgba(255, 255, 255, 0.2)" },
  track: { background: "#73c0ff" },
  handle: { borderColor: "#73c0ff", background: "#fff" },
};

const getSessionDuration = (session: TerminalRecordingSession): number => {
  return (
    session.durationSeconds ??
    session.events[session.events.length - 1]?.atSeconds ??
    0
  );
};

const formatSeconds = (value: number): string => {
  const totalSeconds = Math.max(0, Math.floor(value));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const playerFrameStyle = {
  position: "relative" as const,
  overflow: "hidden" as const,
  background: "#050505",
};

const overlayControlsStyle = {
  position: "absolute" as const,
  left: 16,
  right: 28,
  bottom: 24,
  padding: 12,
  background:
    "linear-gradient(180deg, rgba(8, 8, 8, 0.08) 0%, rgba(8, 8, 8, 0.82) 38%, rgba(8, 8, 8, 0.94) 100%)",
  backdropFilter: "blur(10px)",
  transition: "opacity 160ms ease, transform 160ms ease",
  zIndex: 2,
};

const SingleTerminalRecordingPlayer = ({
  session,
}: {
  session: TerminalRecordingSession;
}) => {
  const dimensions = useMemo(() => inferTerminalDimensions(session), [session]);
  const durationSeconds = getSessionDuration(session);
  const [playheadSeconds, setPlayheadSeconds] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] =
    useState<(typeof PLAYBACK_SPEEDS)[number]>(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showInlineControls, setShowInlineControls] = useState(false);
  const [showFullscreenControls, setShowFullscreenControls] = useState(true);
  const lastTickRef = useRef<number | null>(null);
  const inlineHideTimeoutRef = useRef<number | null>(null);
  const fullscreenHideTimeoutRef = useRef<number | null>(null);

  const clearHideTimeout = useCallback(
    (timeoutRef: MutableRefObject<number | null>) => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    },
    []
  );

  const showControls = useCallback(
    (
      setControls: Dispatch<SetStateAction<boolean>>,
      timeoutRef: MutableRefObject<number | null>
    ) => {
      clearHideTimeout(timeoutRef);
      setControls(true);
    },
    [clearHideTimeout]
  );

  const scheduleHideControls = useCallback(
    (
      setControls: Dispatch<SetStateAction<boolean>>,
      timeoutRef: MutableRefObject<number | null>
    ) => {
      clearHideTimeout(timeoutRef);
      timeoutRef.current = window.setTimeout(() => {
        setControls(false);
        timeoutRef.current = null;
      }, CONTROLS_HIDE_DELAY_MS);
    },
    [clearHideTimeout]
  );

  useEffect(() => {
    setPlayheadSeconds(0);
    setIsPlaying(false);
    lastTickRef.current = null;
    clearHideTimeout(inlineHideTimeoutRef);
    clearHideTimeout(fullscreenHideTimeoutRef);
    setShowInlineControls(false);
    setShowFullscreenControls(true);
  }, [clearHideTimeout, session.sessionId]);

  useEffect(() => {
    return () => {
      clearHideTimeout(inlineHideTimeoutRef);
      clearHideTimeout(fullscreenHideTimeoutRef);
    };
  }, [clearHideTimeout]);

  useEffect(() => {
    if (!isPlaying || durationSeconds <= 0) {
      lastTickRef.current = null;
      return;
    }

    const intervalId = window.setInterval(() => {
      const now = performance.now();
      const lastTick = lastTickRef.current ?? now;
      lastTickRef.current = now;
      const deltaSeconds = ((now - lastTick) / 1000) * playbackSpeed;

      setPlayheadSeconds((currentValue) => {
        const nextValue = currentValue + deltaSeconds;
        if (nextValue >= durationSeconds) {
          window.clearInterval(intervalId);
          setIsPlaying(false);
          lastTickRef.current = null;
          return durationSeconds;
        }
        return nextValue;
      });
    }, 100);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [durationSeconds, isPlaying, playbackSpeed]);

  const handleTogglePlayback = useCallback(() => {
    if (durationSeconds <= 0) {
      return;
    }

    if (playheadSeconds >= durationSeconds) {
      setPlayheadSeconds(0);
      lastTickRef.current = null;
      setIsPlaying(true);
      return;
    }

    setIsPlaying((currentValue) => !currentValue);
  }, [durationSeconds, playheadSeconds]);

  const handleRestart = useCallback(() => {
    setIsPlaying(false);
    setPlayheadSeconds(0);
    lastTickRef.current = null;
  }, []);

  const handleSeek = useCallback((value: number) => {
    setPlayheadSeconds(value);
    lastTickRef.current = null;
  }, []);

  const handlePlaybackSpeedChange = useCallback(
    (value: (typeof PLAYBACK_SPEEDS)[number]) => {
      setPlaybackSpeed(value);
      lastTickRef.current = null;
    },
    []
  );

  const handleOpenFullscreen = useCallback(() => {
    showControls(setShowFullscreenControls, fullscreenHideTimeoutRef);
    setIsFullscreen(true);
  }, [showControls]);
  const handleCloseFullscreen = useCallback(() => {
    clearHideTimeout(fullscreenHideTimeoutRef);
    setShowFullscreenControls(false);
    setIsFullscreen(false);
  }, [clearHideTimeout]);

  const handleInlineMouseMove = useCallback(() => {
    showControls(setShowInlineControls, inlineHideTimeoutRef);
    scheduleHideControls(setShowInlineControls, inlineHideTimeoutRef);
  }, [showControls, scheduleHideControls]);
  const handleInlineMouseLeave = useCallback(
    () => scheduleHideControls(setShowInlineControls, inlineHideTimeoutRef),
    [scheduleHideControls]
  );
  const handleInlineFocusCapture = useCallback(
    () => showControls(setShowInlineControls, inlineHideTimeoutRef),
    [showControls]
  );
  const handleInlineBlurCapture = useCallback(
    () => scheduleHideControls(setShowInlineControls, inlineHideTimeoutRef),
    [scheduleHideControls]
  );

  const handleFullscreenMouseMove = useCallback(() => {
    showControls(setShowFullscreenControls, fullscreenHideTimeoutRef);
    scheduleHideControls(setShowFullscreenControls, fullscreenHideTimeoutRef);
  }, [showControls, scheduleHideControls]);
  const handleFullscreenMouseLeave = useCallback(
    () =>
      scheduleHideControls(setShowFullscreenControls, fullscreenHideTimeoutRef),
    [scheduleHideControls]
  );
  const handleFullscreenFocusCapture = useCallback(
    () => showControls(setShowFullscreenControls, fullscreenHideTimeoutRef),
    [showControls]
  );
  const handleFullscreenBlurCapture = useCallback(
    () =>
      scheduleHideControls(setShowFullscreenControls, fullscreenHideTimeoutRef),
    [scheduleHideControls]
  );

  const renderPlaybackControls = (
    expandedView: boolean,
    controlsVisible: boolean
  ) => {
    return (
      <div
        style={{
          ...overlayControlsStyle,
          opacity: controlsVisible ? 1 : 0,
          transform: controlsVisible ? "translateY(0)" : "translateY(8px)",
          pointerEvents: controlsVisible ? "auto" : "none",
        }}
      >
        <Flex gap="small" align="center" style={{ marginBottom: 12 }}>
          <Button
            aria-label={isPlaying ? "Pause" : "Play"}
            icon={isPlaying ? <PauseOutlined /> : <CaretRightOutlined />}
            onClick={handleTogglePlayback}
            disabled={durationSeconds <= 0}
          />
          <Button
            aria-label="Restart"
            icon={<UndoOutlined />}
            onClick={handleRestart}
          />
          <Button
            aria-label={expandedView ? "Exit fullscreen" : "Enter fullscreen"}
            icon={
              expandedView ? <FullscreenExitOutlined /> : <FullscreenOutlined />
            }
            onClick={
              expandedView ? handleCloseFullscreen : handleOpenFullscreen
            }
          />
          <Select
            aria-label="Playback speed"
            options={PLAYBACK_SPEED_OPTIONS}
            style={{ width: 80 }}
            value={playbackSpeed}
            onChange={handlePlaybackSpeedChange}
            size="small"
          />
          <Typography.Text style={{ color: "rgba(255, 255, 255, 0.88)" }}>
            {formatSeconds(playheadSeconds)} / {formatSeconds(durationSeconds)}
          </Typography.Text>
        </Flex>
        <Slider
          min={0}
          max={Math.max(durationSeconds, 0)}
          step={0.1}
          value={Math.min(playheadSeconds, Math.max(durationSeconds, 0))}
          onChange={handleSeek}
          tooltip={{
            formatter: (value) => formatSeconds(value ?? 0),
          }}
          styles={SLIDER_STYLES}
        />
      </div>
    );
  };

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          background: "#fff",
          paddingBottom: 12,
          marginBottom: 12,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Typography.Paragraph style={{ marginBottom: "8px" }}>
          <Typography.Text strong>{session.sessionId}</Typography.Text>
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary" style={{ marginBottom: "8px" }}>
          {session.startedAt ?? "Unknown start time"}
        </Typography.Paragraph>
        <Typography.Paragraph style={{ marginBottom: "8px" }}>
          <Tag>{session.detectedFormat}</Tag>
          {durationSeconds > 0 && <Tag>{durationSeconds.toFixed(2)}s</Tag>}
          {dimensions.cols && dimensions.rows && (
            <Tag>
              {dimensions.cols}x{dimensions.rows}
            </Tag>
          )}
        </Typography.Paragraph>
      </div>
      <div
        style={playerFrameStyle}
        onMouseMove={handleInlineMouseMove}
        onMouseLeave={handleInlineMouseLeave}
        onFocusCapture={handleInlineFocusCapture}
        onBlurCapture={handleInlineBlurCapture}
      >
        <XtermPlayer
          session={session}
          playheadSeconds={playheadSeconds}
          maxHeight={360}
          showScrollbars={showInlineControls}
        />
        {renderPlaybackControls(false, showInlineControls)}
        {isFullscreen ? (
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0, 0, 0, 0.78)",
              backdropFilter: "blur(7px)",
              zIndex: 3,
            }}
          />
        ) : null}
      </div>
      <Modal
        open={isFullscreen}
        onCancel={handleCloseFullscreen}
        footer={null}
        width="fit-content"
        centered
        destroyOnHidden
        closable={false}
        styles={{
          mask: {
            backdropFilter: "blur(10px)",
            background: "rgba(5, 9, 14, 0.62)",
          },
          content: {
            padding: 12,
            background: "rgba(20, 22, 26, 0.94)",
          },
          body: { padding: 0 },
        }}
      >
        <div
          style={playerFrameStyle}
          onMouseMove={handleFullscreenMouseMove}
          onMouseLeave={handleFullscreenMouseLeave}
          onFocusCapture={handleFullscreenFocusCapture}
          onBlurCapture={handleFullscreenBlurCapture}
        >
          <XtermPlayer
            session={session}
            playheadSeconds={playheadSeconds}
            maxHeight="70vh"
            showScrollbars={showFullscreenControls}
          />
          {renderPlaybackControls(true, showFullscreenControls)}
        </div>
      </Modal>
    </div>
  );
};

const renderSessionItem = (session: TerminalRecordingSession) => {
  return (
    <List.Item key={session.sessionId}>
      <SingleTerminalRecordingPlayer session={session} />
    </List.Item>
  );
};

/** Renders terminal recording sessions without duplicating the surrounding view header. */
export const TerminalRecordingSessionsList = ({
  sessions,
}: {
  sessions: TerminalRecordingSession[];
}) => {
  if (sessions.length === 0) {
    return (
      <Empty
        description="No terminal recordings found"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return <List dataSource={sessions} renderItem={renderSessionItem} />;
};

export const TerminalRecordingSessionView = ({
  view,
  loading,
  getSession,
}: {
  view: TerminalRecordingView;
  loading?: boolean;
  getSession?: () => void;
}) => {
  return (
    <>
      <SessionViewHeader
        view={view}
        fallbackTitle="Session Recordings"
        loading={loading}
        onRefresh={getSession}
      />
      <TerminalRecordingSessionsList sessions={view.sessions} />
    </>
  );
};
